import React from 'react';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import {
	Api,
	BillingInfo,
	CountriesEntity,
	Country,
	CountrySubdivision,
} from '@citrite/sf-api';
import { sort } from '@citrite/sf-sorting';
import { EcommTaxDetails, TaxIds } from '@sharefile/ecomm-models';
import {
	Alert,
	Checkbox,
	Form,
	FormInstance,
	Input,
	Row,
	Skeleton,
	theme,
	Typography,
} from 'antd';
import { useAsync } from 'react-async-hook';
import { FeatureFlag } from '../../featureFlags';
import { t } from '../../translate';
import { handleFormValuesChange, isCountrySupported } from '../components/utils';
import { AddressAutoComplete } from './AddressAutoComplete';
import {
	addressFieldId,
	australianBusinessNumberField,
	cityField,
	europeanVatField,
	gstHSTField,
	narrowCityField,
	postalCodeField,
	provinceField,
	provincialSalesTaxField,
	quebecSalesTaxField,
	stateField,
	zipCodeField,
} from './addressField';
import { subdivisionCountries } from './addressFieldConstants';
import { GetInputAddressField, GetStateOrProvinceField } from './GetAddressField';
import { SearchSelect } from './SearchSelect';
const { sortDirections } = Api;
const { Link, Text } = Typography;
const contact_email = 'com-sharefile@progress.com';

const getCountries = async (): Promise<Country[]> => {
	const countriesRes = await CountriesEntity.get().execute();
	const sortedCountries = getSortedCountries(countriesRes.value || []);
	const usa = sortedCountries.filter(country => {
		return country.Id === 'US';
	});
	sortedCountries.splice(
		sortedCountries.findIndex(({ Id }) => Id === 'US'),
		1
	);
	sortedCountries.unshift(usa[0]);
	return sortedCountries;
};

const getSortedCountries = (countriesList: Country[]) => {
	const filteredCountries = countriesList.filter(c => c.Id !== '*' && c.Id !== '-');
	return sort(filteredCountries, {
		key: 'Name',
		direction: sortDirections.ASCENDING,
	});
};

export interface AddressFormProps {
	addressInfo: BillingInfo;
	updateAddressInfo(addressInfo: BillingInfo, retainInfo?: boolean): void;
	form: FormInstance;
	disableCountries?: boolean;
	isSFHybrisCCInfoSuccessfullyUpdated?: boolean;
	taxValidationError?: Record<TaxIds, boolean>;
	updateTaxDetails?(taxDetails: EcommTaxDetails): void;
	readOnlyTaxFields?: addressFieldId[];
	showTaxDetails?: boolean;
	addressFormDisabled?: boolean;
	testIdPrefix?: string;
}

export const AddressForm = ({
	addressInfo,
	updateAddressInfo,
	form,
	disableCountries,
	isSFHybrisCCInfoSuccessfullyUpdated,
	taxValidationError,
	updateTaxDetails,
	readOnlyTaxFields,
	showTaxDetails = true,
	addressFormDisabled,
	testIdPrefix = '',
}: AddressFormProps) => {
	const { token } = theme.useToken();

	const { loading: loadingCountries, result: countries } = useAsync<
		Country[]
	>(async () => {
		try {
			return await getCountries();
		} catch (error) {
			console.error('Error fetching countries:', error);
			return [];
		}
	}, []);

	const getSortedSubdivisions = (subdivisionsList: CountrySubdivision[]) => {
		const filteredSubdivisions = subdivisionsList.filter(
			c => c.Id !== '*' && c.Id !== '-'
		);
		return sort(filteredSubdivisions, {
			key: 'Name',
			direction: sortDirections.ASCENDING,
		});
	};

	const getSubdivisions = async (value: string) => {
		if (!addressInfo.Country || !subdivisionCountries.includes(addressInfo.Country)) {
			return [];
		}
		const countryId = CountriesEntity.get(value).getUrl();
		const country = await CountriesEntity.get(countryId).execute();

		const sortedSubdivisions = getSortedSubdivisions(country.Subdivisions || []);
		if (!addressInfo?.State) {
			if (sortedSubdivisions.length > 0) {
				updateAddressInfo({ State: sortedSubdivisions[0]?.Id ?? '' });
			}
		}
		return sortedSubdivisions;
	};

	const { loading: loadingSubdivisions, result: subdivisions } = useAsync<
		CountrySubdivision[]
	>(async () => {
		return await getSubdivisions(addressInfo.Country || 'US');
	}, [addressInfo.Country]);

	const isCountryDisabled = disableCountries || isSFHybrisCCInfoSuccessfullyUpdated;
	const shouldAllowCountryChange =
		getFeatureFlagValue(FeatureFlag.EnableInternationalEcomm, false) ||
		isCountrySupported(addressInfo.Country);

	if (loadingCountries || loadingSubdivisions) {
		return <Skeleton active />;
	}

	return (
		<Form
			form={form}
			layout="vertical"
			onValuesChange={changedValues =>
				handleFormValuesChange(
					changedValues,
					updateAddressInfo,
					updateTaxDetails,
					addressInfo.Country
				)
			}
			data-testid="address-form"
			disabled={addressFormDisabled}
		>
			<SearchSelect
				addressFieldId={addressFieldId.country}
				required
				options={countries!.map(country => ({
					value: country.Id,
					label: country.Name,
				}))}
				disableEditing={isCountryDisabled}
				dataTestId={`${testIdPrefix}country`}
				label={t('billing_address.country')}
				rules={[
					{
						required: true,
						message: t('billing_address.validation_message.country_required'),
					},
				]}
				tooltip={
					isCountryDisabled ? (
						<Text style={{ color: token.colorWhite }}>
							{t('billing_address.country_not_supported_message', {
								sales_team: (
									<Link
										href="https://www.sharefile.com/contact-us"
										target="_blank"
										rel="noopener noreferrer"
										style={{ color: token.colorWhite }}
										underline
									>
										{t('common.sales_team')}
									</Link>
								),
							})}
						</Text>
					) : undefined
				}
			/>

			{shouldAllowCountryChange ? (
				<>
					{getFeatureFlagValue(FeatureFlag.EnableBillingAutocomplete) ? (
						<AddressAutoComplete
							country={addressInfo.Country}
							form={form}
							onAddressSelected={updateAddressInfo}
							testPrefixId={testIdPrefix}
						/>
					) : (
						<Form.Item
							name={addressFieldId.addressLine1}
							label={t('billing_address.street')}
							required
							rules={[
								{
									required: true,
									message: t('billing_address.validation_message.street_required'),
								},
							]}
						>
							<Input data-testid={`${testIdPrefix}street-address`} />
						</Form.Item>
					)}
					<CityPostalCodeRow
						addressInfo={addressInfo}
						addressFormDisabled={addressFormDisabled}
						testIdPrefix={testIdPrefix}
					/>
					{subdivisions && (
						<ProvinceStateZipPostalCodeRow
							addressInfo={addressInfo}
							subdivisions={subdivisions}
							testIdPrefix={testIdPrefix}
							disableFormEditing={addressFormDisabled}
						/>
					)}
					{subdivisions && showTaxDetails && (
						<TaxInfoRow
							addressInfo={addressInfo}
							readOnlyTaxFields={readOnlyTaxFields}
							taxValidationError={taxValidationError}
							disableFormEditing={addressFormDisabled}
							testIdPrefix={testIdPrefix}
						/>
					)}
				</>
			) : (
				<Alert
					message={t('billing_address.disable_trial_conversion_message', {
						live_chat: (
							<Link
								href="https://support.sharefile.com/s/"
								target="_blank"
								rel="noopener noreferrer"
								style={{ color: token.colorText }}
								underline
							>
								{t('common.live_chat')}
							</Link>
						),
					})}
					type="info"
					showIcon
					data-testid={`${testIdPrefix}disable-trial-conversion-message`}
				/>
			)}
		</Form>
	);
};

//Just City if US or CA, otherwise City and Postal Code
export const CityPostalCodeRow: React.FC<{
	addressInfo: BillingInfo;
	addressFormDisabled?: boolean;
	testIdPrefix?: string;
}> = ({ addressInfo, addressFormDisabled, testIdPrefix }) => {
	if (addressInfo.Country === 'US' || addressInfo.Country === 'CA') {
		return (
			<Row gutter={12}>
				<GetInputAddressField
					addressInfo={addressInfo}
					addressField={cityField}
					validationMessages={{
						required: t('billing_address.validation_message.city_required'),
					}}
					disableEditing={addressFormDisabled}
					testIdPrefix={testIdPrefix}
				/>
			</Row>
		);
	}

	return (
		<Row gutter={12} justify="space-between">
			<GetInputAddressField
				addressInfo={addressInfo}
				addressField={narrowCityField}
				validationMessages={{
					required: t('billing_address.validation_message.city_required'),
				}}
				disableEditing={addressFormDisabled}
				testIdPrefix={testIdPrefix}
			/>
			<GetInputAddressField
				addressInfo={addressInfo}
				addressField={postalCodeField}
				disableEditing={addressFormDisabled}
				testIdPrefix={testIdPrefix}
			/>
		</Row>
	);
};

export const ProvinceStateZipPostalCodeRow: React.FC<{
	addressInfo: BillingInfo;
	subdivisions: CountrySubdivision[];
	disableFormEditing?: boolean;
	testIdPrefix?: string;
}> = ({ addressInfo, subdivisions, disableFormEditing, testIdPrefix }) => {
	if (addressInfo.Country !== 'US' && addressInfo.Country !== 'CA') {
		return null;
	}
	return (
		<Row gutter={12}>
			{addressInfo.Country === 'US' && (
				<>
					<GetStateOrProvinceField
						addressInfo={addressInfo}
						addressField={stateField}
						validationMessages={{
							required: t('billing_address.validation_message.state_required'),
						}}
						subdivisions={subdivisions}
						testIdPrefix={testIdPrefix}
						disableEditing={disableFormEditing}
					/>
					<GetInputAddressField
						addressInfo={addressInfo}
						addressField={zipCodeField}
						validationMessages={{
							required: t('billing_address.validation_message.zip_required'),
						}}
						disableEditing={disableFormEditing}
						testIdPrefix={testIdPrefix}
					/>
				</>
			)}
			{addressInfo.Country === 'CA' && (
				<>
					<GetStateOrProvinceField
						addressInfo={addressInfo}
						addressField={provinceField}
						validationMessages={{
							required: t('billing_address.validation_message.province_required'),
						}}
						subdivisions={subdivisions}
						testIdPrefix={testIdPrefix}
						disableEditing={disableFormEditing}
					/>
					<GetInputAddressField
						addressInfo={addressInfo}
						addressField={postalCodeField}
						disableEditing={disableFormEditing}
						testIdPrefix={testIdPrefix}
					/>
				</>
			)}
		</Row>
	);
};

export const TaxInfoRow: React.FC<{
	addressInfo: BillingInfo;
	readOnlyTaxFields?: addressFieldId[];
	taxValidationError?: Record<TaxIds, boolean>;
	disableFormEditing?: boolean;
	testIdPrefix?: string;
}> = ({
	addressInfo,
	readOnlyTaxFields = [],
	taxValidationError,
	disableFormEditing,
	testIdPrefix,
}) => {
	const [isSLFI, setSLFI] = React.useState<boolean>(false);
	const { token } = theme.useToken();
	if (addressInfo.Country === 'US') {
		return null;
	}
	return (
		<>
			{addressInfo.State === 'QC' && (
				<Row>
					<Form.Item>
						<Checkbox onChange={() => setSLFI(!isSLFI)} disabled={disableFormEditing}>
							{t('billing_address.sfi_message')}
						</Checkbox>
					</Form.Item>
				</Row>
			)}
			<Row gutter={12} justify={'space-between'}>
				<GetInputAddressField
					addressInfo={addressInfo}
					addressField={australianBusinessNumberField}
					validationMessages={{
						invalid: t('billing_address.validation_message.tax_invalid'),
					}}
					toolTipText={t('billing_address.tax_tooltip')}
					disableEditing={
						readOnlyTaxFields.includes(addressFieldId.australianBusinessNumber) ||
						disableFormEditing
					}
					error={taxValidationError?.ABN}
					testIdPrefix={testIdPrefix}
				/>
				<GetInputAddressField
					addressInfo={addressInfo}
					addressField={europeanVatField}
					validationMessages={{
						invalid: t('billing_address.validation_message.tax_invalid'),
					}}
					toolTipText={t('billing_address.tax_tooltip')}
					disableEditing={
						disableFormEditing || readOnlyTaxFields.includes(addressFieldId.europeanVATId)
					}
					error={taxValidationError?.EuropeanVATID}
					testIdPrefix={testIdPrefix}
				/>
				<GetInputAddressField
					addressInfo={addressInfo}
					addressField={gstHSTField}
					validationMessages={{
						invalid: t('billing_address.validation_message.tax_invalid'),
					}}
					toolTipText={t('billing_address.tax_tooltip')}
					disableEditing={
						disableFormEditing || readOnlyTaxFields.includes(addressFieldId.gstHSTNumber)
					}
					error={taxValidationError?.GSTID}
					testIdPrefix={testIdPrefix}
				/>
				<GetInputAddressField
					addressInfo={addressInfo}
					addressField={provincialSalesTaxField}
					validationMessages={{
						invalid: t('billing_address.validation_message.tax_invalid'),
					}}
					toolTipText={t('billing_address.tax_tooltip')}
					disableEditing={
						disableFormEditing ||
						readOnlyTaxFields.includes(addressFieldId.provincialSalesTax)
					}
					error={taxValidationError?.PSTID}
					testIdPrefix={testIdPrefix}
				/>
				{!isSLFI && (
					<GetInputAddressField
						addressInfo={addressInfo}
						addressField={quebecSalesTaxField}
						validationMessages={{
							invalid: t('billing_address.validation_message.tax_invalid'),
						}}
						toolTipText={t('billing_address.tax_tooltip')}
						disableEditing={
							disableFormEditing ||
							readOnlyTaxFields.includes(addressFieldId.quebecSalesTax)
						}
						error={taxValidationError?.QSTID}
						testIdPrefix={testIdPrefix}
					/>
				)}
			</Row>
			{addressInfo.Country === 'CA' && (
				<Alert
					message={t('billing_address.buy_canada_tax_free')}
					description={
						<>
							{t('billing_address.buy_canada_tax_free_description', {
								contact_email: (
									<Link
										href={`mailto:${contact_email}`}
										style={{ color: token.colorText }}
										underline
									>
										{contact_email}
									</Link>
								),
							})}
						</>
					}
					type="info"
					showIcon
					data-testid={`${testIdPrefix}canada-province-alert`}
				/>
			)}
		</>
	);
};
